import React from "react";
import { NoAnswersSurveyAnswerRatioBar, QuestionSurveyAnswerRatioBar } from "./SurveyAnswerRatioBar";
import styled from "styled-components";
import { QuestionReportDataSet, SurveySegment, CommentData, SurveyQuestion, assertNever } from "../../../../../../../amorg-common";
import { getSurveyComments } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { AnimatedCollapsibleContent } from "../../../../../../../designSystem/collapsible/AnimatedCollapsibleContent";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { ListRow } from "../../../../../../../designSystem/compositeComponents/ListRow";
import { NoDataSurveyAnswerScoreRadialChart, SurveyQuestionAnswerScoreRadialChart } from "../../../../../../../designSystem/graphs/SurveyAnswerScoreRadialChart";
import { Icon } from "../../../../../../../designSystem/icons/Icons";
import { IndicatorList } from "./IndicatorList";
import { surveyQuestionHelper } from "../../../../../../../amorg-common/helpers/surveyQuestionHelper";
import { colors } from "../../../../../../../designSystem/colors/color";


type QuestionPanelProps = {
    surveyId: string,
    siteId: string,
    data: QuestionReportDataSet,
    segmentFilter: SurveySegment[][],
    question:SurveyQuestion,
    showCommentsButton: boolean,
    showDetails?: boolean,
}
export function QuestionPanel(props: QuestionPanelProps): JSX.Element {
    const [comments, setComments] = React.useState<CommentData[] | undefined>(undefined);

    React.useEffect(() => {
        const update = async () => {
            if(comments !== undefined)
            {
                const comments = await getSurveyComments(props.siteId, props.surveyId, props.segmentFilter, props.data.themeIndex, props.data.themeQuestionIndex);
                setComments(comments);
            }
        };
        update();
    }, [props.showDetails, props.segmentFilter]);

    async function handleShowComments() {
        const comments = await getSurveyComments(props.siteId, props.surveyId, props.segmentFilter, props.data.themeIndex, props.data.themeQuestionIndex);
        setComments(comments);
    }

    const optionColors:{selectionValue:string, color:string}[] = React.useMemo(() => {
        const options = surveyQuestionHelper.getOptions(props.data.question)
        if(surveyQuestionHelper.isScoredQuestion(props.question))
        {
            const scoreMapping = surveyQuestionHelper.getScoreMapping(props.question);
            return options.map(option => 
            {
                const score = scoreMapping.find(s => s.value === option.value)?.score;
                let color = colors.graphs.survey.noScore;

                if(score === undefined)
                    color = color;
                else if(score === 1)
                    color = colors.graphs.survey.score.score1;
                else if(score === 2)
                    color = colors.graphs.survey.score.score2;
                else if(score === 3)
                    color = colors.graphs.survey.score.score3;
                else if(score === 4)
                    color = colors.graphs.survey.score.score4;
                else if(score === 5)
                    color = colors.graphs.survey.score.score5;
                else
                    assertNever(score);

                return { selectionValue: option.value, color };
            });
        }
        else {
            return options.map((o,i) => {
                return { selectionValue: o.value, color: colors.indexed.dark[i % colors.indexed.dark.length] };
            });
        }
    }, [props.data.question]);


    return (
        <div>
            <ListRow styleVariant={props.question.conditionalDisplay ? "lightGray" : "neutral"} sizeVariant="slim" 
                level={props.question.conditionalDisplay ? "level3" : "level2"}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:32, flexGrow:1}}>

                    <div style={{display:'flex', justifyContent:'flex-start', alignItems:'center', width:'200px'}}>
                        <QuestionNumber>#{(props.data.absoluteQuestionIndex + 1)}</QuestionNumber>

                        {props.data.stats.totalSelections === 0
                            ? <NoDataSurveyAnswerScoreRadialChart small />
                            : <SurveyQuestionAnswerScoreRadialChart small question={props.data.question} stats={props.data.stats} />
                        }

                        <Flex alignItems="center" gap={2}>
                            {props.data.commentCount}
                            <Icon size="x-small" name={'interface_glyph_comment'} />
                        </Flex>

                    </div>

                    <div style={{display:'flex', flexDirection:'column', flexGrow:1, gap:8}}>
                        <QuestionTitle>{props.data.question.title}</QuestionTitle>

                        <div style={{ width: '100%' }}>

                            {props.data.stats.totalSelections === 0
                                ? <NoAnswersSurveyAnswerRatioBar />
                                : <QuestionSurveyAnswerRatioBar stats={props.data.stats} question={props.data.question} small />
                            }
                        </div>

                        {/* {showDetailedGraph
                            ? <AnswerRatioBarGraph data={data} legend='none' showValueText />
                            : <GroupedAnswerRatioBarGraph data={data} legend='none' showValueText />
                        } */}
                        <Flex style={{height:24}} justifyContent="space-between" alignItems="center" gap={8}>
                            <IndicatorList indicators={props.data.question.indicators} />
                            {props.data.commentCount > 0 &&
                                <Button variant='text'
                                    size="22px"
                                    disabled={props.data.commentCount === 0}
                                    icon={ comments===undefined ? 'arrows_chevron_chevronDown' : 'arrows_chevron_chevronUp' } 
                                    text={ comments===undefined ? 'Vis kommentarer' : 'Skjul kommentarer' }
                                    onClick={() => comments===undefined ? handleShowComments() : setComments(undefined) } />
                            }
                        </Flex>
                    </div>

                </div>
            </ListRow>
            <AnimatedCollapsibleContent open={comments !== undefined}>
                {comments !== undefined &&
                    <>
                        {comments.sort((a, b) => a.selectionValue.localeCompare(b.selectionValue)).map((c,i) => 
                        {
                            return (
                                <ListRow key={i} styleVariant="neutral" sizeVariant="slim" level="level3">
                                    <Flex gap={5}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            color:'white',
                                            backgroundColor:optionColors.find(a => a.selectionValue === c.selectionValue)?.color || colors.graphs.survey.noScore,
                                            borderRadius: '50%',
                                            width: '20px',
                                            height: '20px',
                                            flexShrink:0
                                            }}></div>
                                        <div>{c.text}</div>
                                    </Flex>
                                </ListRow>
                            );
                        }
                        )}
                    </>
                }
            </AnimatedCollapsibleContent>
        </div>
    );
}

const QuestionNumber = styled.div`
    color: rgba(191,191,191,1);
    font-size:1rem;
    font-weight: 700;
    line-height: 1.5rem;
    margin-right: 5px;
    flex:0;
`;

const QuestionTitle = styled.div`
    overflow-wrap: break-word;
    white-space: pre-wrap; 
    font-weight: 600;
`;